import React from 'react';
import { Helmet } from 'react-helmet-async';
import logoImage from 'figma:asset/1ae33b9aef646c65e83975c5787f479260f08591.webp';

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  keywords?: string;
  robots?: string;
}

export function SEO({
  title = "Top Dog Grooming North Suffolk VA | Wiggy's Wags & Wiggles",
  description = "Expert dog grooming, Goldendoodle, Labradoodle, Poodle, Spaniel & Custom Breed Grooming in North Suffolk, VA (23435). Also offering in-home overnight dog boarding & doggie daycare near Harbour View.",
  image = logoImage,
  url = typeof window !== 'undefined' ? window.location.href : "https://wiggyswagsandwiggles.com",
  keywords,
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
}: SEOProps) {

  // Primary High-Priority Search Intent Terms
  const PRIORITY_SEARCH_TERMS = [
    // --- TIER 1: HIGH PRIORITY (SUITE OF TOP SEARCH TERMS) ---
    "Dog Groomer Suffolk VA 23435",
    "Drop off Groomer Suffolk VA",
    "Dog Grooming Harbour View Suffolk VA",
    "Dog Groomer Cedar Point",
    "Groomer near Driver VA",
    "Dog Groomer near Burbage Grant",
    "Dog Groomer near Riverfront at Harbour View",
    "Groomer near Governors Pointe Drive",
    "In-Home Dog Boarding Suffolk VA",
    "Doggie Daycare North Suffolk 23435",
    "Overnight Dog Boarding Harbour View",
    "Groomer Bennett Creek",
    "Grooomer Cedar Point",
    "Groomer near Western Branch North",
    "Groomer near Governors Pointe Drive",
    "Groomer near Cedar Point",
    "Groomer near Driver",
    "Groomer near Bennett Creek",
    "Groomer near Riverfront at Harbour View",
    "Dog Groomer near Burbage Grant",
    "Dog Groomer Cedar Point",
    "Dog Groomer Bennett Creek",
    "Dog Groomer near Western Branch North",
    "Dog Groomer near Governors Pointe Drive",
    "Dog Groomer near Cedar Point",
    "Dog Groomer near Driver",
    "Dog Groomer near Bennett Creek",
    "Dog Groomer near Riverfront at Harbour View",
    "Dog Groomer near Burbage Grant",
    "In Home Mobile Groomer N Suffolk VA",
    "Dog groomer near suffolk",
    "Groomer near Suffolk",
    "dog grooming near Suffolk",
    "Dog Grooming N Suffolk",
    "Mobile Dog Groomer N Suffolk",
    "N Suffolk Groomer",
    "N Suffolk Dog Groomer",
    "Best Dog Grooming near Suffolk",
    "Top Grooming near Suffolk",
    "Dog Grooming Harbour View Suffolk VA",
    "Best Dog Groomer Riverfront Suffolk",
    "North Suffolk Dog Boarding and Grooming",
    "Luxury Pet Groomer Harbour View VA",
    "Groomer Harbour View",
    "Dog Groomer Harbour View",
    "Groomer N Suffolk",
    "Dog Groomer N Suffolk",
    "Groomer Riverfront Suffolk",
    "Dog Groomer Riverfront Suffolk VA",

    // --- TIER 2: SUFFOLK & HYPER-LOCAL NEIGHBORHOODS ---
    "Groomer Western Branch North Chesapeake",
    "Dog Groomer 23321",
    "Churchland Portsmouth dog grooming",
    "Bridge Road Suffolk VA pet care",
    "Shoulders Hill Rd dog groomer",
    "Doodle grooming specialists North Suffolk",
    "Goldendoodle haircuts Suffolk VA",
    "Labradoodle grooming 23435",
    "Local puppy grooming North Suffolk",
    "Private dog boarding fenced yard Suffolk",
    "dog groomer near me Suffolk VA",
    "best dog grooming North Suffolk",
    "dog groomer near Harbour View VA",
    "dog grooming Bridge Road Suffolk VA",
    "Bennett's Creek dog grooming services",
    "Driver VA pet groomers near me",
    "dog wash and haircut Shoulders Hill Rd",
    "Full Service Dog Grooming Suffolk",
    "Groomer near Suffolk VA",
    "Groomer near N Suffolk",
    "Dog Groomer near N Suffolk",
    "Best dog grooming near Suffolk VA",
    "Top rated dog groomers Suffolk",
    "Affordable puppy grooming Suffolk",
    "Local dog shampoo and trim Suffolk",
    "Doodle grooming specialists Suffolk VA",
    "Mobile alternative dog grooming Suffolk",
    "Dog grooming Harbour View near me",
    "Local pet groomers Suffolk VA",
    "Puppy grooming services Suffolk",
    "Dog nail clipping and ear cleaning Suffolk",
    "Affordable dog grooming Suffolk VA",
    "Professional dog styling Suffolk",
    "Dog Grooming near Bridge Road Suffolk VA",
    "Best Dog Groomer Bennett's Creek",
    "North Suffolk Pet Grooming near Driver VA",
    "Dog Groomer near 23435",
    "Knotts Neck dog grooming services",
    "Quick dog nail trim near Harbour View",
    "Burbage Grant dog wash",
    "Dog grooming near College Run Suffolk",
    "Riverfront at Harbour View dog groomer",
    "Governors Pointe pet grooming",
    "Cedar Point dog haircut",
    "Nansemond Pointe deshedding",
    "Sleepy Hole Pointe dog bath",
    "goldendoodle haircuts North Suffolk",
    "labradoodle grooming near me 23435",
    "poodle haircut specialists Suffolk",
    "small dog bath and trim Suffolk VA",
    "large dog deshedding treatment Suffolk",
    "drop off dog groomer North Suffolk",
    "in-home dog grooming alternative Suffolk",

    // --- TIER 3: CHESAPEAKE & WESTERN BRANCH / HICKORY / CENTERVILLE ---
    "Dog Groomer Chesapeake VA",
    "Dog Grooming Portsmouth VA",
    "Pet Groomer North Suffolk Hampton Roads",
    "Affordable Dog Grooming Chesapeake",
    "Groomer Chesapeake",
    "Groomer Western Branch",
    "Groomer Hickory",
    "Groomer Centerville",
    "Dog Groomer near Chesapeake VA",
    "Best dog grooming Chesapeake Virginia",
    "Top rated pet groomers Chesapeake",
    "Doodle and Poodle grooming Chesapeake",
    "Same day dog grooming Chesapeake",
    "Local puppy grooming salon Chesapeake",
    "Dog Grooming Hickory Chesapeake VA",
    "Best Dog Groomer Centerville Chesapeake",
    "Top Rated Pet Grooming Hickory VA",
    "Puppy Grooming Centerville VA",
    "Dog grooming Hickory Chesapeake VA near me",
    "Dog groomer near me Centerville Chesapeake",
    "Pet grooming 23322 near me",
    "Local dog groomers Hickory VA near me",
    "Top Grooming Hickory Chesapeake VA",
    "Chesapeake VA dog grooming salon",
    "Full service pet groomer Chesapeake",
    "Same day dog grooming Greenbrier Chesapeake",
    "Western Branch Chesapeake dog groomer 23321",
    "dog nail trim Western Branch Chesapeake",

    // --- TIER 4: PORTSMOUTH ---
    "Luxury Dog Grooming Portsmouth VA",
    "Groomer Portsmouth",
    "Dog Groomer near Portsmouth VA",
    "Best dog grooming Portsmouth",
    "Affordable grooming Portsmouth",
    "Top rated pet grooming Portsmouth VA",
    "Same day dog groomer Portsmouth",
    "Local pet groomers Portsmouth VA",
    "Dog grooming Portsmouth VA near me",
    "Puppy grooming services Portsmouth",
    "Express dog bath and brush Portsmouth",
    "Churchland Portsmouth dog grooming",

    // --- TIER 5: NORFOLK & VIRGINIA BEACH ---
    "Professional Dog Grooming Norfolk VA",
    "Groomer Norfolk",
    "Groomer Virginia Beach",
    "Groomer VB VA",
    "Same Day Dog Grooming Virginia Beach",
    "Dog grooming Norfolk VA near me",
    "Best dog groomer Norfolk Virginia",
    "Top rated dog grooming Virginia Beach",
    "Dog groomer near me Virginia Beach oceanfront",
    "Same day dog grooming VB",
    "Affordable pet groomer Norfolk",
    "Ghent Norfolk dog grooming services",
    "Chix Beach dog groomer Virginia Beach",
    "Kempsville dog groomer Virginia Beach",

    // --- TIER 6: NEWPORT NEWS & HAMPTON ---
    "Expert Dog Groomer Newport News",
    "Groomer Newport News",
    "Dog groomer near me Newport News",
    "Best dog grooming Hampton VA",
    "Affordable grooming Newport News airport",
    "Top rated pet groomers Hampton Roads",
    "Local dog grooming salon Hampton VA",
    "Newport News puppy grooming packages",
    "Dog nail trimming trim Hampton VA",
    "Dog Nail Trimming Hampton",

    // --- TIER 7: SECONDARY BOARDING & DAYCARE (20%) ---
    "Safe Dog Boarding Suffolk VA",
    "Overnight Dog Boarding Norfolk",
    "Fenced Backyard Dog Boarding Virginia Beach",
    "Private Dog Boarding Chesapeake",
    "Reliable Overnight Pet Care Portsmouth",
    "Best Dog Sitting Hampton",
    "Dog Sitting and Boarding Newport News",
    "In-Home Dog Boarding Hampton Roads",
    "Dog Boarding Chesapeake 23322",
    "doggie daycare North Suffolk 23435",
    "overnight dog sitting Harbour View",
    "dog boarding with fenced yard Suffolk"
  ];

  // --- TIER 8: SPECIALTY BREED GROOMING ---
    "Poodle grooming specialists North Suffolk",
    "Goldendoodle and Labradoodle haircuts VA",
    "Cockapoo grooming services Suffolk",
    "Shih Tzu and Maltese puppy cut Suffolk",
    "Lhasa Apso grooming North Suffolk",
    "Cocker Spaniel grooming",
    "Cavalier King Charles Spaniel grooming VA",
    "Yorkshire and Silky Terrier hair care",
    "Samoyed and Husky de-shedding treatment Suffolk",
    "Malamute grooming",
    "Schnauzer beard and skirt trim Suffolk"

  const computedKeywords = keywords || PRIORITY_SEARCH_TERMS.join(", ");

  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "PetGrooming",
    "@id": "https://wiggyswagsandwiggles.com/#organization",
    "name": "Wiggy's Wags and Wiggles",
    "alternateName": [
      "Wiggy's Wags & Wiggles",
      "Drop off Groomer Suffolk VA",
      "In Home Mobile Groomer N Suffolk VA",
      "Dog groomer near suffolk",
      "Groomer near Suffolk",
      "dog grooming near Suffolk",
      "Dog Grooming N Suffolk",
      "Mobile Dog Groomer N Suffolk",
      "N Suffolk Groomer",
      "N Suffolk Dog Groomer",
      "Best Dog Grooming near Suffolk",
      "Top Grooming near Suffolk",
      "Dog Grooming Harbour View Suffolk VA",
      "Best Dog Groomer Riverfront Suffolk",
      "North Suffolk Dog Boarding and Grooming",
      "Luxury Pet Groomer Harbour View VA",
      "Best Dog Groomer Suffolk VA",
      "Drop-off Dog Groomer N Suffolk",
      "In-Home Dog Grooming N Suffolk",
      "Hampton Roads Dog Grooming",
      "Full Service Dog Grooming Suffolk",
      "Dog Groomer near Bridge Road",
      "North Suffolk Dog Grooming 23435",
      "Harbour View Pet Grooming",
      "Bennetts Creek Dog Boarding",
      "Driver VA Dog Grooming",
      "Dog groomer near Riverfront Suffolk",
      "dog grooming North Suffolk VA"
    ],
    "image": image,
    "logo": image,
    "description": description,
    "url": url,
    "telephone": "+1-872-357-6339",
    "email": "jasonewigman2@gmail.com",
    "priceRange": "$20-$100",
    "paymentAccepted": "Cash, Credit Card, Debit Card, Venmo, Zelle",
    "currenciesAccepted": "USD",
    "slogan": "Best Dog Grooming & Pet Care North Suffolk VA 23435",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4552 Shoulder Hills Rd",
      "addressLocality": "Suffolk",
      "addressRegion": "VA",
      "postalCode": "23435",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 36.827687,
      "longitude": -76.480970
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "07:00",
        "closes": "19:00"
      }
    ],
    "areaServed": [
      {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": 36.827687,
          "longitude": -76.480970
        },
        "geoRadius": "16093.4"
      },
      { "@type": "City", "name": "Suffolk", "sameAs": "https://en.wikipedia.org/wiki/Suffolk,_Virginia" },
      { "@type": "City", "name": "Chesapeake", "sameAs": "https://en.wikipedia.org/wiki/Chesapeake,_Virginia" },
      { "@type": "City", "name": "Portsmouth", "sameAs": "https://en.wikipedia.org/wiki/Portsmouth,_Virginia" },
      { "@type": "City", "name": "Virginia Beach", "sameAs": "https://en.wikipedia.org/wiki/Virginia_Beach,_Virginia" },
      { "@type": "City", "name": "Norfolk", "sameAs": "https://en.wikipedia.org/wiki/Norfolk,_Virginia" },
      { "@type": "City", "name": "Hampton", "sameAs": "https://en.wikipedia.org/wiki/Hampton,_Virginia" },
      { "@type": "City", "name": "Newport News", "sameAs": "https://en.wikipedia.org/wiki/Newport_News,_Virginia" },
      { "@type": "AdministrativeArea", "name": "The Riverfront at Harbour View" },
      { "@type": "AdministrativeArea", "name": "Governor's Pointe" },
      { "@type": "AdministrativeArea", "name": "Cedar Point" },
      { "@type": "AdministrativeArea", "name": "Harbour View" },
      { "@type": "AdministrativeArea", "name": "Burbage Grant" },
      { "@type": "AdministrativeArea", "name": "North Suffolk" },
      { "@type": "AdministrativeArea", "name": "Bennett's Creek" },
      { "@type": "AdministrativeArea", "name": "Driver" },
      { "@type": "AdministrativeArea", "name": "Western Branch" },
      { "@type": "PostalCode", "name": "23435" },
      { "@type": "PostalCode", "name": "23321" },
      { "@type": "PostalCode", "name": "23322" }
    ],
    "knowsAbout": [
      "Dog Grooming",
      "Dog Groomer Near Me",
      "Full Service Dog Grooming",
      "Doodle Grooming Specialists",
      "Goldendoodle Grooming",
      "Labradoodle Haircuts",
      "Poodle Grooming",
      "Deshedding",
      "Dog Bath and Blowdry",
      "Nail Trimming and Grinding",
      "Ear Cleaning and Plucking",
      "Puppy Grooms",
      "Large Breed Dog Grooming",
      "Small Dog Grooming",
      "Mobile Grooming",
      "Drop-off Grooming Services",
      "In-Home Dog Boarding",
      "Doggie Daycare",
      "Overnight Pet Sitting"
    ],
    "founder": {
      "@type": "Person",
      "name": "Jason Ewigman",
      "jobTitle": "Owner & Professional Dog Care Specialist",
      "email": "jasonewigman2@gmail.com",
      "telephone": "+1-872-357-6339"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Dog Grooming & Pet Care Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Full Service Dog Grooming & Haircuts",
            "description": "Professional dog grooming in North Suffolk, VA (23435). Custom breed haircuts, doodle trims, baths, deshedding, nail clipping, and ear cleaning."
          },
          "price": "60.00",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "In-Home & Mobile Alternative Dog Grooming",
            "description": "Convenient in-home or drop-off mobile-alternative grooming sessions tailored to North Suffolk, Harbour View, and surrounding communities."
          },
          "price": "100.00",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Overnight Dog Boarding",
            "description": "Private in-home overnight dog boarding featuring a large secure fenced backyard in North Suffolk VA."
          },
          "price": "45.00",
          "priceCurrency": "USD"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Doggie Daycare",
            "description": "Daytime pet care with supervised play in a spacious fenced yard."
          },
          "price": "25.00",
          "priceCurrency": "USD"
        }
      ]
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What areas do you serve for dog grooming in Hampton Roads?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We serve North Suffolk (23435), Harbour View, The Riverfront, Governor's Pointe, Cedar Point, Burbage Grant, Driver, Western Branch Chesapeake (23321), Portsmouth, Norfolk, Virginia Beach, Newport News, and Hampton."
        }
      },
      {
        "@type": "Question",
        "name": "Where are you located in Suffolk VA?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We are conveniently located at 4552 Shoulder Hills Rd in North Suffolk, VA (23435), right near Bridge Road and Harbour View."
        }
      },
      {
        "@type": "Question",
        "name": "What services are included in a full dog groom?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our full-service grooming package includes a soothing bath, blow dry, custom haircut or sanitary trim, deshedding treatment, nail trimming, and ear cleaning."
        }
      },
      {
        "@type": "Question",
        "name": "Do you specialize in doodle grooming?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! We specialize in Goldendoodles, Labradoodles, Poodles, and all curly coated breed cuts and maintenance."
        }
      },
      {
        "@type": "Question",
        "name": "Do you offer overnight dog boarding?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! We offer limited in-home overnight dog boarding and doggie daycare featuring a large fenced backyard."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://wiggyswagsandwiggles.com/"
      },
      ...(url !== "https://wiggyswagsandwiggles.com" && url !== "https://wiggyswagsandwiggles.com/" ? [
        {
          "@type": "ListItem",
          "position": 2,
          "name": url.split('/').pop()?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || "Page",
          "item": url
        }
      ] : [])
    ]
  };

  return (
    <Helmet>
      {/* Favicon */}
      <link rel="icon" type="image/png" href={logoImage} />
      <link rel="apple-touch-icon" href={logoImage} />
      
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={computedKeywords} />
      <meta name="robots" content={robots} />

      {/* Local Geographic targeting */}
      <meta name="geo.region" content="US-VA" />
      <meta name="geo.placename" content="Suffolk, Virginia" />
      <meta name="geo.position" content="36.827687;-76.480970" />
      <meta name="ICBM" content="36.827687, -76.480970" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="Wiggy's Wags and Wiggles" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Canonical Link */}
      <meta name="author" content="Jason Ewigman" />
      <link rel="canonical" href={url} />

      {/* Structured JSON-LD Schemas */}
      <script type="application/ld+json">{JSON.stringify(businessSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
    </Helmet>
  );
}
